"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations, type TranslationKeys } from "@/lib/translations";

export type Language = "en" | "es";

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = "solariis_language";
const COOKIE_KEY = "solariis_language";

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
  /** When true, use defaultLanguage directly without reading localStorage/cookie */
  controlled?: boolean;
}

export function LanguageProvider({
  children,
  defaultLanguage = "es",
  controlled = false,
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [isInitialized, setIsInitialized] = useState(controlled);

  // Load language from localStorage or cookie on mount (skip in controlled mode)
  useEffect(() => {
    if (controlled) return;
    if (typeof window !== "undefined") {
      const storedLanguage = localStorage.getItem(STORAGE_KEY) as Language | null;
      if (storedLanguage && (storedLanguage === "en" || storedLanguage === "es")) {
        setLanguageState(storedLanguage);
      } else {
        // Fallback to cookie if localStorage is empty
        const cookieMatch = document.cookie.match(
          new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]*)`)
        );
        const cookieLang = cookieMatch?.[1] as Language | undefined;
        if (cookieLang && (cookieLang === "en" || cookieLang === "es")) {
          setLanguageState(cookieLang);
          localStorage.setItem(STORAGE_KEY, cookieLang);
        }
      }
      setIsInitialized(true);
    }
  }, [controlled]);

  // Persist language to localStorage and cookie when it changes
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, lang);
      document.cookie = `${COOKIE_KEY}=${lang};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    }
  }, []);

  // Get current translations object
  const t = useMemo(() => translations[language], [language]);

  // Prevent hydration mismatch by not rendering until initialized
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguageContext must be used within a LanguageProvider");
  }
  return context;
}
